import { Dispatch } from '@reduxjs/toolkit';
import { AppThunk } from '../store';
import {
	asyncCallStart,
	asyncCallError,
	asyncCallDone,
	fetchCompanyExternalUserSuccess,
	fetchCompanyExternalUsersSuccess,
	createCompanyExternalUserSuccess,
	updateCompanyExternalUserSuccess,
} from '../stores/companyExternalUser';
import * as CompanyExternalUserService from '../../services/company-external-user.service';
import {
	CreateCompanyExternalUserDTO,
	UpdateCompanyExternalUserDTO,
	CompanyExternalUserFilter,
} from '../models/companyExternalUser';
import { showAlert } from '../stores/alertStore';

export const fetchCompanyExternalUsers =
	(filter: CompanyExternalUserFilter): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const response =
				await CompanyExternalUserService.getCompanyExternalUsers(
					filter
				);
			dispatch(fetchCompanyExternalUsersSuccess(response));
		} catch (error) {
			console.error('Error fetching company external users:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message:
						'Failed to fetch external users. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const fetchCompanyExternalUser =
	(id: string): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const user =
				await CompanyExternalUserService.getCompanyExternalUser(id);
			dispatch(fetchCompanyExternalUserSuccess(user));
		} catch (error) {
			console.error('Error fetching company external user:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to fetch external user. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const fetchCompanyExternalUsersByCompany =
	(companyId: string): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const users =
				await CompanyExternalUserService.getCompanyExternalUsersByCompany(
					companyId
				);
			dispatch(
				fetchCompanyExternalUsersSuccess({
					list: users,
					count: users.length,
				})
			);
		} catch (error) {
			console.error(
				'Error fetching company external users by company:',
				error
			);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message:
						'Failed to fetch external users. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const createCompanyExternalUser =
	(input: CreateCompanyExternalUserDTO): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const user =
				await CompanyExternalUserService.createCompanyExternalUser(
					input
				);
			dispatch(createCompanyExternalUserSuccess(user));
			dispatch(
				showAlert({
					message: 'External user successfully created.',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error creating company external user:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message:
						'Failed to create external user. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const updateCompanyExternalUser =
	(id: string, input: UpdateCompanyExternalUserDTO): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const user =
				await CompanyExternalUserService.updateCompanyExternalUser(
					id,
					input
				);
			dispatch(updateCompanyExternalUserSuccess(user));
			dispatch(
				showAlert({
					message: 'External user successfully updated.',
					severity: 'success',
				})
			);
		} catch (error) {
			console.error('Error updating company external user:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message:
						'Failed to update external user. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};

export const resendExternalUserInvite =
	(id: string, customMessage?: string): AppThunk =>
	async (dispatch: Dispatch) => {
		dispatch(asyncCallStart());
		try {
			const success =
				await CompanyExternalUserService.resendExternalUserInvite(
					id,
					customMessage
				);
			if (success) {
				dispatch(
					showAlert({
						message: 'Invitation sent successfully.',
						severity: 'success',
					})
				);
				// Optionally refresh the user list to get updated status
			} else {
				throw new Error('Failed to send invitation');
			}
		} catch (error) {
			console.error('Error sending invitation:', error);
			dispatch(asyncCallError(error));
			dispatch(
				showAlert({
					message: 'Failed to send invitation. Please try again.',
					severity: 'error',
				})
			);
		}
		dispatch(asyncCallDone());
	};
